import { sql } from "drizzle-orm";
import {
  pgTable,
  text,
  varchar,
  timestamp,
  boolean,
  unique,
  integer,
  jsonb,
} from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const permissionStatusOptions = [
  "share_only",
  "open_adopt",
  "private",
] as const;
export const messagePrivacyOptions = ["friends_only", "everyone"] as const;
export const notificationSoundOptions = ["none", "chime", "pop", "bell", "whoosh", "notify", "808", "scratch", "hihat", "boombap"] as const;
export const messageSoundOptions = ["none", "ding", "bubble", "soft", "alert", "hihat", "scratch", "boombap"] as const;
export const barTypeOptions = ["single_bar", "snippet", "half_verse"] as const;
export const moderationStatusOptions = [
  "approved",
  "pending_review",
  "flagged",
  "blocked",
] as const;
export const phraseSeverityOptions = ["block", "flag"] as const;
export const userRoleOptions = ["artist", "producer", "both"] as const;
export const beatGenreOptions = ["trap", "boom_bap", "drill", "lo_fi", "west_coast", "east_coast", "melodic", "dark", "experimental", "other"] as const;
export const beatLicenseOptions = ["preview_only", "open_use", "credit_required"] as const;
export const beatStatusOptions = ["pending", "approved", "rejected"] as const;
export const songStatusOptions = ["draft", "released"] as const;
export const sectionTypeOptions = ["intro", "verse", "hook", "bridge", "outro", "ad_lib"] as const;

export const users = pgTable("users", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  username: text("username").notNull().unique(),
  displayName: text("display_name"),
  email: text("email").notNull().unique(),
  emailVerified: boolean("email_verified").notNull().default(false),
  password: text("password").notNull(),
  bio: text("bio"),
  location: text("location"),
  avatarUrl: text("avatar_url"),
  bannerUrl: text("banner_url"),
  membershipTier: text("membership_tier").notNull().default("free"),
  membershipExpiresAt: timestamp("membership_expires_at"),
  proStartDate: timestamp("pro_start_date"),
  showProCard: boolean("show_pro_card").notNull().default(true),
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  isAdmin: boolean("is_admin").notNull().default(false),
  isAdminPlus: boolean("is_admin_plus").notNull().default(false),
  isOwner: boolean("is_owner").notNull().default(false),
  usernameChangedAt: timestamp("username_changed_at"),
  onlineStatus: text("online_status").notNull().default("offline"),
  lastSeenAt: timestamp("last_seen_at"),
  messagePrivacy: text("message_privacy").notNull().default("friends_only"),
  notificationSound: text("notification_sound").notNull().default("chime"),
  messageSound: text("message_sound").notNull().default("ding"),
  displayedBadges: text("displayed_badges").array(),
  xp: integer("xp").notNull().default(0),
  level: integer("level").notNull().default(1),
  lastXpUpdate: timestamp("last_xp_update").defaultNow(),
  dailyXpLikes: integer("daily_xp_likes").notNull().default(0),
  dailyXpComments: integer("daily_xp_comments").notNull().default(0),
  dailyXpBookmarks: integer("daily_xp_bookmarks").notNull().default(0),
  supabaseId: text("supabase_id"),
  userRole: text("user_role").notNull().default("artist"),
  isProducer: boolean("is_producer").notNull().default(false),
  producerVerified: boolean("producer_verified").notNull().default(false),
  producerVerifiedAt: timestamp("producer_verified_at"),
  lastLoginAt: timestamp("last_login_at"),
});

export const verificationCodes = pgTable("verification_codes", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  email: text("email").notNull(),
  code: text("code").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const passwordResetCodes = pgTable("password_reset_codes", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  email: text("email").notNull(),
  code: text("code").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const usersRelations = relations(users, ({ many }) => ({
  bars: many(bars),
}));

export const bars = pgTable("bars", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  explanation: text("explanation"),
  category: text("category").notNull(),
  tags: text("tags").array(),
  feedbackWanted: boolean("feedback_wanted").notNull().default(false),
  isOriginal: boolean("is_original").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  proofBarId: text("proof_bar_id"),
  permissionStatus: text("permission_status").notNull().default("share_only"),
  proofHash: text("proof_hash"),
  isFeatured: boolean("is_featured").notNull().default(false),
  featuredAt: timestamp("featured_at"),
  barType: text("bar_type").notNull().default("single_bar"),
  fullRapLink: text("full_rap_link"),
  beatLink: text("beat_link"),
  isRecorded: boolean("is_recorded").notNull().default(false),
  moderationStatus: text("moderation_status").notNull().default("approved"),
  moderationScore: integer("moderation_score"),
  moderationPhraseId: varchar("moderation_phrase_id"),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references(() => users.id),
  deletedReason: text("deleted_reason"),
  isLocked: boolean("is_locked").notNull().default(false),
  lockedAt: timestamp("locked_at"),
  promptSlug: text("prompt_slug"),
  promptText: text("prompt_text"),
  parentBarId: varchar("parent_bar_id").references(() => bars.id, { onDelete: "set null" }),
});

export const barsRelations = relations(bars, ({ one, many }) => ({
  user: one(users, {
    fields: [bars.userId],
    references: [users.id],
  }),
  likes: many(likes),
  comments: many(comments),
  parentBar: one(bars, {
    fields: [bars.parentBarId],
    references: [bars.id],
    relationName: "parentReply",
  }),
  replies: many(bars, { relationName: "parentReply" }),
}));

export const likes = pgTable("likes", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  barId: varchar("bar_id")
    .notNull()
    .references(() => bars.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const likesRelations = relations(likes, ({ one }) => ({
  user: one(users, { fields: [likes.userId], references: [users.id] }),
  bar: one(bars, { fields: [likes.barId], references: [bars.id] }),
}));

export const comments = pgTable("comments", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  barId: varchar("bar_id")
    .notNull()
    .references(() => bars.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const commentsRelations = relations(comments, ({ one, many }) => ({
  user: one(users, { fields: [comments.userId], references: [users.id] }),
  bar: one(bars, { fields: [comments.barId], references: [bars.id] }),
  likes: many(commentLikes),
}));

export const commentLikes = pgTable(
  "comment_likes",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    commentId: varchar("comment_id")
      .notNull()
      .references(() => comments.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [unique("comment_likes_unique").on(table.userId, table.commentId)],
);

export const commentLikesRelations = relations(commentLikes, ({ one }) => ({
  user: one(users, { fields: [commentLikes.userId], references: [users.id] }),
  comment: one(comments, {
    fields: [commentLikes.commentId],
    references: [comments.id],
  }),
}));

export const dislikes = pgTable(
  "dislikes",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    barId: varchar("bar_id")
      .notNull()
      .references(() => bars.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [unique("dislikes_unique").on(table.userId, table.barId)],
);

export const dislikesRelations = relations(dislikes, ({ one }) => ({
  user: one(users, { fields: [dislikes.userId], references: [users.id] }),
  bar: one(bars, { fields: [dislikes.barId], references: [bars.id] }),
}));

export const commentDislikes = pgTable(
  "comment_dislikes",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    commentId: varchar("comment_id")
      .notNull()
      .references(() => comments.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    unique("comment_dislikes_unique").on(table.userId, table.commentId),
  ],
);

export const commentDislikesRelations = relations(
  commentDislikes,
  ({ one }) => ({
    user: one(users, {
      fields: [commentDislikes.userId],
      references: [users.id],
    }),
    comment: one(comments, {
      fields: [commentDislikes.commentId],
      references: [comments.id],
    }),
  }),
);

export const follows = pgTable(
  "follows",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    followerId: varchar("follower_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    followingId: varchar("following_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [unique("follows_unique").on(table.followerId, table.followingId)],
);

export const followsRelations = relations(follows, ({ one }) => ({
  follower: one(users, {
    fields: [follows.followerId],
    references: [users.id],
  }),
  following: one(users, {
    fields: [follows.followingId],
    references: [users.id],
  }),
}));

export const notifications = pgTable("notifications", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  type: text("type").notNull(),
  actorId: varchar("actor_id").references(() => users.id, {
    onDelete: "cascade",
  }),
  barId: varchar("bar_id").references(() => bars.id, { onDelete: "cascade" }),
  commentId: varchar("comment_id").references(() => comments.id, {
    onDelete: "cascade",
  }),
  message: text("message").notNull(),
  read: boolean("read").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const bookmarks = pgTable(
  "bookmarks",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    barId: varchar("bar_id")
      .notNull()
      .references(() => bars.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [unique("bookmarks_unique").on(table.userId, table.barId)],
);

export const bookmarksRelations = relations(bookmarks, ({ one }) => ({
  user: one(users, { fields: [bookmarks.userId], references: [users.id] }),
  bar: one(bars, { fields: [bookmarks.barId], references: [bars.id] }),
}));

export const pushSubscriptions = pgTable("push_subscriptions", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  endpoint: text("endpoint").notNull().unique(),
  p256dh: text("p256dh").notNull(),
  auth: text("auth").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const friendships = pgTable(
  "friendships",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    requesterId: varchar("requester_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    receiverId: varchar("receiver_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    status: text("status").notNull().default("pending"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    unique("friendships_unique").on(table.requesterId, table.receiverId),
  ],
);

export const friendshipsRelations = relations(friendships, ({ one }) => ({
  requester: one(users, {
    fields: [friendships.requesterId],
    references: [users.id],
  }),
  receiver: one(users, {
    fields: [friendships.receiverId],
    references: [users.id],
  }),
}));

export const directMessages = pgTable("direct_messages", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  senderId: varchar("sender_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  receiverId: varchar("receiver_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  readAt: timestamp("read_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const directMessagesRelations = relations(directMessages, ({ one }) => ({
  sender: one(users, {
    fields: [directMessages.senderId],
    references: [users.id],
  }),
  receiver: one(users, {
    fields: [directMessages.receiverId],
    references: [users.id],
  }),
}));

export const adoptions = pgTable(
  "adoptions",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    originalBarId: varchar("original_bar_id")
      .notNull()
      .references(() => bars.id, { onDelete: "cascade" }),
    adoptedByBarId: varchar("adopted_by_bar_id")
      .notNull()
      .references(() => bars.id, { onDelete: "cascade" }),
    adoptedByUserId: varchar("adopted_by_user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    unique("adoptions_unique").on(table.originalBarId, table.adoptedByBarId),
  ],
);

export const adoptionsRelations = relations(adoptions, ({ one }) => ({
  originalBar: one(bars, {
    fields: [adoptions.originalBarId],
    references: [bars.id],
  }),
  adoptedByBar: one(bars, {
    fields: [adoptions.adoptedByBarId],
    references: [bars.id],
  }),
  adoptedByUser: one(users, {
    fields: [adoptions.adoptedByUserId],
    references: [users.id],
  }),
}));

export const barUsages = pgTable("bar_usages", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  barId: varchar("bar_id")
    .notNull()
    .references(() => bars.id, { onDelete: "cascade" }),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  usageLink: text("usage_link"),
  comment: text("comment"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const barUsagesRelations = relations(barUsages, ({ one }) => ({
  bar: one(bars, { fields: [barUsages.barId], references: [bars.id] }),
  user: one(users, { fields: [barUsages.userId], references: [users.id] }),
}));

export const barSequence = pgTable("bar_sequence", {
  id: varchar("id").primaryKey().default("singleton"),
  currentValue: integer("current_value").notNull().default(0),
});

export const userAchievements = pgTable(
  "user_achievements",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    achievementId: text("achievement_id").notNull(),
    unlockedAt: timestamp("unlocked_at").notNull().defaultNow(),
  },
  (table) => [
    unique("user_achievements_unique").on(table.userId, table.achievementId),
  ],
);

export const userAchievementsRelations = relations(
  userAchievements,
  ({ one }) => ({
    user: one(users, {
      fields: [userAchievements.userId],
      references: [users.id],
    }),
  }),
);

export const reportReasonOptions = [
  "illegal_content",
  "harassment",
  "spam",
  "other",
] as const;
export const reportStatusOptions = [
  "pending",
  "reviewed",
  "dismissed",
  "action_taken",
] as const;

export const reports = pgTable("reports", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  reporterId: varchar("reporter_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  barId: varchar("bar_id").references(() => bars.id, { onDelete: "cascade" }),
  commentId: varchar("comment_id").references(() => comments.id, {
    onDelete: "cascade",
  }),
  userId: varchar("user_id").references(() => users.id, {
    onDelete: "cascade",
  }),
  reason: text("reason").notNull(),
  details: text("details"),
  status: text("status").notNull().default("pending"),
  reviewedBy: varchar("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const reportsRelations = relations(reports, ({ one }) => ({
  reporter: one(users, {
    fields: [reports.reporterId],
    references: [users.id],
  }),
  bar: one(bars, { fields: [reports.barId], references: [bars.id] }),
  comment: one(comments, {
    fields: [reports.commentId],
    references: [comments.id],
  }),
  reviewedByUser: one(users, {
    fields: [reports.reviewedBy],
    references: [users.id],
  }),
}));

export const flaggedPhrases = pgTable("flagged_phrases", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  phrase: text("phrase").notNull(),
  normalizedPhrase: text("normalized_phrase").notNull(),
  severity: text("severity").notNull().default("flag"),
  similarityThreshold: integer("similarity_threshold").notNull().default(80),
  notes: text("notes"),
  isActive: boolean("is_active").notNull().default(true),
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const flaggedPhrasesRelations = relations(flaggedPhrases, ({ one }) => ({
  creator: one(users, {
    fields: [flaggedPhrases.createdBy],
    references: [users.id],
  }),
}));

// AI moderation review requests - when AI rejects a bar but user wants manual review
export const aiReviewRequests = pgTable("ai_review_requests", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  category: text("category").notNull(),
  tags: text("tags").array(),
  explanation: text("explanation"),
  barType: text("bar_type").notNull().default("single_bar"),
  beatLink: text("beat_link"),
  fullRapLink: text("full_rap_link"),
  aiRejectionReasons: text("ai_rejection_reasons").array(),
  plagiarismRisk: text("plagiarism_risk"),
  plagiarismDetails: text("plagiarism_details"),
  userAppeal: text("user_appeal"), // User's explanation for why it should be approved
  status: text("status").notNull().default("pending"), // pending, approved, rejected
  reviewedBy: varchar("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const aiReviewRequestsRelations = relations(
  aiReviewRequests,
  ({ one }) => ({
    user: one(users, {
      fields: [aiReviewRequests.userId],
      references: [users.id],
    }),
    reviewer: one(users, {
      fields: [aiReviewRequests.reviewedBy],
      references: [users.id],
    }),
  }),
);

export type AchievementRarity = "common" | "rare" | "epic" | "legendary";

// Condition types for custom achievements
export const achievementConditionTypes = [
  "bars_posted", // Total bars posted >= threshold
  "likes_received", // Total likes received >= threshold
  "followers_count", // Followers >= threshold
  "following_count", // Following >= threshold
  "single_bar_likes", // Any single bar has >= threshold likes
  "single_bar_comments", // Any single bar has >= threshold comments
  "single_bar_bookmarks", // Any single bar has >= threshold bookmarks
  "comments_made", // Total comments made >= threshold
  "bars_adopted", // Total bars adopted >= threshold
  "controversial_bar", // Has a bar with more dislikes than likes (threshold = min total reactions)
  "night_owl", // Posted a bar between midnight and 5am
  "early_bird", // Posted a bar between 5am and 8am
  "bars_with_keyword", // Bars containing a specific keyword >= threshold
] as const;

export type AchievementConditionType =
  (typeof achievementConditionTypes)[number];

export const achievementApprovalStatusOptions = [
  "pending",
  "approved",
  "rejected",
] as const;

// Rule tree types for compound achievement conditions
export type AchievementCondition = {
  type: "condition";
  metric: AchievementConditionType;
  comparator: ">=" | ">" | "=" | "<" | "<=";
  value: number;
  keyword?: string; // For bars_with_keyword metric
  negated?: boolean; // For NOT logic - inverts the comparison
  timeRange?: { start: number; end: number }; // For time-based metrics (hour in 24h format)
};

export type AchievementRuleGroup = {
  type: "group";
  operator: "AND" | "OR";
  children: (AchievementCondition | AchievementRuleGroup)[];
};

export type AchievementRuleTree = AchievementCondition | AchievementRuleGroup;

export const customAchievements = pgTable("custom_achievements", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  emoji: text("emoji"), // Deprecated - use imageUrl instead
  imageUrl: text("image_url"), // Custom badge image URL
  description: text("description").notNull(),
  rarity: text("rarity").notNull().default("common"),
  conditionType: text("condition_type").notNull(),
  threshold: integer("threshold").notNull().default(1),
  ruleTree: jsonb("rule_tree"), // For compound conditions (AND/OR logic)
  isActive: boolean("is_active").notNull().default(true),
  approvalStatus: text("approval_status").notNull().default("approved"), // pending, approved, rejected
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const customAchievementsRelations = relations(
  customAchievements,
  ({ one }) => ({
    creator: one(users, {
      fields: [customAchievements.createdBy],
      references: [users.id],
    }),
  }),
);

export type CustomAchievement = typeof customAchievements.$inferSelect;
export type InsertCustomAchievement = typeof customAchievements.$inferInsert;

// Debug logs for admin troubleshooting
export const debugLogs = pgTable("debug_logs", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  action: text("action").notNull(), // like, dislike, follow, etc.
  userId: varchar("user_id"),
  targetId: varchar("target_id"), // barId, userId, etc.
  details: text("details").notNull(), // JSON string with full details
  success: boolean("success").notNull().default(true),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export type DebugLog = typeof debugLogs.$inferSelect;
export type InsertDebugLog = typeof debugLogs.$inferInsert;

export const ACHIEVEMENTS = {
  // Original achievements
  first_bar: {
    name: "Origin Founder",
    emoji: "🔥",
    imageUrl: null as string | null,
    description: "Posted your first bar",
    threshold: { barsMinted: 1 },
    rarity: "common" as AchievementRarity,
  },
  bar_slinger: {
    name: "Bar Slinger",
    emoji: "💀",
    imageUrl: null as string | null,
    description: "Posted 10 bars",
    threshold: { barsMinted: 10 },
    rarity: "rare" as AchievementRarity,
  },
  bar_lord: {
    name: "Bar Lord",
    emoji: "👑",
    imageUrl: null as string | null,
    description: "Posted 50 bars",
    threshold: { barsMinted: 50 },
    rarity: "epic" as AchievementRarity,
  },
  crowd_pleaser: {
    name: "Crowd Pleaser",
    emoji: "🎤",
    imageUrl: null as string | null,
    description: "Received 100 total likes",
    threshold: { likesReceived: 100 },
    rarity: "rare" as AchievementRarity,
  },
  cult_leader: {
    name: "Cult Leader",
    emoji: "🪖",
    imageUrl: null as string | null,
    description: "Gained 50 followers",
    threshold: { followers: 50 },
    rarity: "epic" as AchievementRarity,
  },
  immortal_bar: {
    name: "Immortal",
    emoji: "🌹",
    imageUrl: null as string | null,
    description: "One bar reached 500 likes",
    threshold: { topBarLikes: 500 },
    rarity: "legendary" as AchievementRarity,
  },
  milsling_legacy: {
    name: "Milsling Heir",
    emoji: "⚔️",
    imageUrl: null as string | null,
    description: "Received 1000 total likes",
    threshold: { likesReceived: 1000 },
    rarity: "legendary" as AchievementRarity,
  },
  wordsmith: {
    name: "Wordsmith",
    emoji: "✍️",
    imageUrl: null as string | null,
    description: "Posted 25 bars",
    threshold: { barsMinted: 25 },
    rarity: "rare" as AchievementRarity,
  },
  rising_star: {
    name: "Rising Star",
    emoji: "⭐",
    imageUrl: null as string | null,
    description: "Gained 10 followers",
    threshold: { followers: 10 },
    rarity: "common" as AchievementRarity,
  },
  viral: {
    name: "Viral",
    emoji: "🔥",
    imageUrl: null as string | null,
    description: "One bar reached 100 likes",
    threshold: { topBarLikes: 100 },
    rarity: "rare" as AchievementRarity,
  },

  // Engagement & Virality Boosters
  viral_bronze: {
    name: "Viral Bronze",
    emoji: "🥉",
    imageUrl: null as string | null,
    description: "One bar reached 50 likes + 10 bookmarks",
    threshold: { topBarEngagement: 60 },
    rarity: "rare" as AchievementRarity,
  },
  viral_silver: {
    name: "Viral Silver",
    emoji: "🥈",
    imageUrl: null as string | null,
    description: "One bar reached 200 likes + 50 bookmarks",
    threshold: { topBarEngagement: 250 },
    rarity: "epic" as AchievementRarity,
  },
  viral_gold: {
    name: "Viral Gold",
    emoji: "🥇",
    imageUrl: null as string | null,
    description: "One bar reached 500 likes + 100 bookmarks",
    threshold: { topBarEngagement: 600 },
    rarity: "legendary" as AchievementRarity,
  },
  thread_dominator: {
    name: "Thread Dominator",
    emoji: "💬",
    imageUrl: null as string | null,
    description: "One bar sparked 20+ comments",
    threshold: { topBarComments: 20 },
    rarity: "epic" as AchievementRarity,
  },
  reply_king: {
    name: "Reply King",
    emoji: "🗣️",
    imageUrl: null as string | null,
    description: "Made 50 comments on others' bars",
    threshold: { commentsMade: 50 },
    rarity: "rare" as AchievementRarity,
  },
  adopted_remixed: {
    name: "Adopted & Remixed",
    emoji: "🔄",
    imageUrl: null as string | null,
    description: "Your bars got adopted 5+ times",
    threshold: { barsAdoptedByOthers: 5 },
    rarity: "epic" as AchievementRarity,
  },

  // Consistency & Grind Streaks
  streak_starter: {
    name: "Streak Starter",
    emoji: "🔥",
    imageUrl: null as string | null,
    description: "Posted bars 7 days in a row",
    threshold: { streakDays: 7 },
    rarity: "common" as AchievementRarity,
  },
  streak_master: {
    name: "Streak Master",
    emoji: "⚡",
    imageUrl: null as string | null,
    description: "Posted bars 14 days in a row",
    threshold: { streakDays: 14 },
    rarity: "rare" as AchievementRarity,
  },
  streak_legend: {
    name: "Streak Legend",
    emoji: "🌟",
    imageUrl: null as string | null,
    description: "Posted bars 30 days in a row",
    threshold: { streakDays: 30 },
    rarity: "epic" as AchievementRarity,
  },
  streak_immortal: {
    name: "Streak Immortal",
    emoji: "💎",
    imageUrl: null as string | null,
    description: "Posted bars 100 days in a row",
    threshold: { streakDays: 100 },
    rarity: "legendary" as AchievementRarity,
  },
  volume_spitter: {
    name: "Volume Spitter",
    emoji: "🎯",
    imageUrl: null as string | null,
    description: "Posted 100 bars total",
    threshold: { barsMinted: 100 },
    rarity: "epic" as AchievementRarity,
  },
  bar_machine: {
    name: "Bar Machine",
    emoji: "🏭",
    imageUrl: null as string | null,
    description: "Posted 500 bars total",
    threshold: { barsMinted: 500 },
    rarity: "legendary" as AchievementRarity,
  },
  bar_god: {
    name: "Bar God",
    emoji: "⚡",
    imageUrl: null as string | null,
    description: "Posted 1000 bars total",
    threshold: { barsMinted: 1000 },
    rarity: "legendary" as AchievementRarity,
  },
  weekend_warrior: {
    name: "Weekend Warrior",
    emoji: "🌙",
    imageUrl: null as string | null,
    description: "Posted 5+ bars on a weekend",
    threshold: { weekendBars: 5 },
    rarity: "common" as AchievementRarity,
  },
  midnight_marauder: {
    name: "Midnight Marauder",
    emoji: "🦇",
    imageUrl: null as string | null,
    description: "Posted 3+ bars between midnight and 5am",
    threshold: { midnightBars: 3 },
    rarity: "rare" as AchievementRarity,
  },

  // Community & Social
  mentor: {
    name: "Mentor",
    emoji: "🎓",
    imageUrl: null as string | null,
    description: "Adopted 20+ bars from others",
    threshold: { adoptionsGiven: 20 },
    rarity: "epic" as AchievementRarity,
  },
  bar_adopter: {
    name: "Bar Adopter",
    emoji: "🤝",
    imageUrl: null as string | null,
    description: "Adopted 5 bars from others",
    threshold: { adoptionsGiven: 5 },
    rarity: "common" as AchievementRarity,
  },
  comment_goat: {
    name: "Comment GOAT",
    emoji: "🐐",
    imageUrl: null as string | null,
    description: "Received 100+ likes on your comments",
    threshold: { commentLikesReceived: 100 },
    rarity: "epic" as AchievementRarity,
  },
  crew_builder: {
    name: "Crew Builder",
    emoji: "👥",
    imageUrl: null as string | null,
    description: "Gained 100+ followers",
    threshold: { followers: 100 },
    rarity: "epic" as AchievementRarity,
  },
  influencer: {
    name: "Influencer",
    emoji: "📢",
    imageUrl: null as string | null,
    description: "Gained 500+ followers",
    threshold: { followers: 500 },
    rarity: "legendary" as AchievementRarity,
  },
  network_king: {
    name: "Network King",
    emoji: "🕸️",
    imageUrl: null as string | null,
    description: "Following 100+ active posters",
    threshold: { following: 100 },
    rarity: "rare" as AchievementRarity,
  },

  // Platform Health
  clean_streak: {
    name: "Clean Streak",
    emoji: "✨",
    imageUrl: null as string | null,
    description: "90 days without violations",
    threshold: { cleanDays: 90 },
    rarity: "rare" as AchievementRarity,
  },
  rule_follower: {
    name: "Rule Follower",
    emoji: "📜",
    imageUrl: null as string | null,
    description: "180 days without violations",
    threshold: { cleanDays: 180 },
    rarity: "epic" as AchievementRarity,
  },
  model_citizen: {
    name: "Model Citizen",
    emoji: "🏆",
    imageUrl: null as string | null,
    description: "365 days without violations",
    threshold: { cleanDays: 365 },
    rarity: "legendary" as AchievementRarity,
  },

  // Underdog & Special
  underdog: {
    name: "Underdog",
    emoji: "🐕",
    imageUrl: null as string | null,
    description: "Got 50+ likes on a bar with <50 followers",
    threshold: { underdogBar: 1 },
    rarity: "epic" as AchievementRarity,
  },
  og_member: {
    name: "OG Member",
    emoji: "🎖️",
    imageUrl: null as string | null,
    description: "Account older than 1 year",
    threshold: { accountAgeDays: 365 },
    rarity: "rare" as AchievementRarity,
  },
  veteran: {
    name: "Veteran",
    emoji: "🎗️",
    imageUrl: null as string | null,
    description: "Account older than 2 years",
    threshold: { accountAgeDays: 730 },
    rarity: "epic" as AchievementRarity,
  },

  // Challenge achievements
  challenge_contender: {
    name: "Challenge Contender",
    emoji: "⚔️",
    imageUrl: null as string | null,
    description: "Competed in your first challenge",
    threshold: { challengesEntered: 1 },
    rarity: "common" as AchievementRarity,
  },
} as const;

// Achievement badge images stored in database for built-in achievements
export const achievementBadgeImages = pgTable("achievement_badge_images", {
  id: varchar("id").primaryKey(), // matches the achievement key (e.g., "first_bar")
  imageUrl: text("image_url").notNull(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type AchievementBadgeImage = typeof achievementBadgeImages.$inferSelect;

export const tagAnimationOptions = [
  "none",
  "pulse",
  "glow",
  "shimmer",
  "bounce",
  "sparkle",
  "gradient",
] as const;
export type TagAnimation = (typeof tagAnimationOptions)[number];

export const customTags = pgTable("custom_tags", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  displayName: text("display_name"),
  imageUrl: text("image_url"),
  animation: text("animation").notNull().default("none"),
  color: text("color"),
  backgroundColor: text("background_color"),
  isActive: boolean("is_active").notNull().default(true),
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const customTagsRelations = relations(customTags, ({ one }) => ({
  creator: one(users, {
    fields: [customTags.createdBy],
    references: [users.id],
  }),
}));

export type CustomTag = typeof customTags.$inferSelect;
export type InsertCustomTag = typeof customTags.$inferInsert;

export const insertCustomTagSchema = createInsertSchema(customTags).omit({
  id: true,
  createdAt: true,
});

export const customCategories = pgTable("custom_categories", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  displayName: text("display_name"),
  imageUrl: text("image_url"),
  color: text("color"),
  backgroundColor: text("background_color"),
  sortOrder: integer("sort_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const customCategoriesRelations = relations(
  customCategories,
  ({ one }) => ({
    creator: one(users, {
      fields: [customCategories.createdBy],
      references: [users.id],
    }),
  }),
);

export type CustomCategory = typeof customCategories.$inferSelect;
export type InsertCustomCategory = typeof customCategories.$inferInsert;

export const insertCustomCategorySchema = createInsertSchema(
  customCategories,
).omit({
  id: true,
  createdAt: true,
});

export type AchievementId = keyof typeof ACHIEVEMENTS;

// Profile Badges - Custom badges that appear next to usernames
export const profileBadges = pgTable("profile_badges", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  displayName: text("display_name").notNull(),
  description: text("description"),
  imageUrl: text("image_url"),
  emoji: text("emoji"),
  color: text("color"), // text color
  backgroundColor: text("background_color"),
  borderColor: text("border_color"),
  animation: text("animation").notNull().default("none"),
  rarity: text("rarity").notNull().default("common"), // common, rare, epic, legendary
  isActive: boolean("is_active").notNull().default(true),
  linkedAchievementId: text("linked_achievement_id"), // links to achievement key if earned from achievement
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const profileBadgesRelations = relations(profileBadges, ({ one }) => ({
  creator: one(users, {
    fields: [profileBadges.createdBy],
    references: [users.id],
  }),
}));

export type ProfileBadge = typeof profileBadges.$inferSelect;
export type InsertProfileBadge = typeof profileBadges.$inferInsert;

export const insertProfileBadgeSchema = createInsertSchema(profileBadges).omit({
  id: true,
  createdAt: true,
});

// User Badges - Junction table for which users own which badges
export const userBadges = pgTable("user_badges", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  badgeId: varchar("badge_id")
    .notNull()
    .references(() => profileBadges.id, { onDelete: "cascade" }),
  source: text("source").notNull().default("owner_gift"), // "achievement", "owner_gift", "purchase", "event"
  sourceDetails: text("source_details"), // e.g., achievement ID or event name
  grantedBy: varchar("granted_by").references(() => users.id), // who gave the badge (for owner gifts)
  grantedAt: timestamp("granted_at").notNull().defaultNow(),
});

export const userBadgesRelations = relations(userBadges, ({ one }) => ({
  user: one(users, { fields: [userBadges.userId], references: [users.id] }),
  badge: one(profileBadges, {
    fields: [userBadges.badgeId],
    references: [profileBadges.id],
  }),
  granter: one(users, {
    fields: [userBadges.grantedBy],
    references: [users.id],
  }),
}));

export type UserBadge = typeof userBadges.$inferSelect;
export type InsertUserBadge = typeof userBadges.$inferInsert;

export const notificationsRelations = relations(notifications, ({ one }) => ({
  user: one(users, { fields: [notifications.userId], references: [users.id] }),
  actor: one(users, {
    fields: [notifications.actorId],
    references: [users.id],
  }),
  bar: one(bars, { fields: [notifications.barId], references: [bars.id] }),
}));

export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  membershipTier: true,
  membershipExpiresAt: true,
  stripeCustomerId: true,
  stripeSubscriptionId: true,
});

export const insertBarSchema = createInsertSchema(bars).omit({
  id: true,
  createdAt: true,
  proofBarId: true,
  proofHash: true,
});

export const updateBarSchema = z.object({
  content: z.string().min(1).max(2000).optional(),
  explanation: z.string().max(500).optional().nullable(),
  category: z
    .enum([
      "Funny",
      "Serious",
      "Wordplay",
      "Storytelling",
      "Battle",
      "Freestyle",
    ])
    .optional(),
  tags: z.array(z.string()).optional(),
  feedbackWanted: z.boolean().optional(),
  barType: z.enum(["single_bar", "snippet", "half_verse"]).optional(),
  fullRapLink: z
    .string()
    .optional()
    .nullable()
    .transform((v) => (v === "" ? null : v))
    .pipe(z.string().url().nullable().optional()),
  beatLink: z
    .string()
    .optional()
    .nullable()
    .transform((v) => (v === "" ? null : v))
    .pipe(z.string().url().nullable().optional()),
});

export const insertCommentSchema = createInsertSchema(comments).omit({
  id: true,
  createdAt: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;
export type InsertBar = z.infer<typeof insertBarSchema>;
export type Bar = typeof bars.$inferSelect;
export type Like = typeof likes.$inferSelect;
export type Comment = typeof comments.$inferSelect;
export type CommentLike = typeof commentLikes.$inferSelect;
export type InsertComment = z.infer<typeof insertCommentSchema>;
export type Follow = typeof follows.$inferSelect;
export type Notification = typeof notifications.$inferSelect;
export type Bookmark = typeof bookmarks.$inferSelect;
export type PushSubscription = typeof pushSubscriptions.$inferSelect;
export type Friendship = typeof friendships.$inferSelect;
export type DirectMessage = typeof directMessages.$inferSelect;
export type Adoption = typeof adoptions.$inferSelect;
export type BarUsage = typeof barUsages.$inferSelect;
export type UserAchievement = typeof userAchievements.$inferSelect;
export type Report = typeof reports.$inferSelect;
export type FlaggedPhrase = typeof flaggedPhrases.$inferSelect;

export const onlineStatusOptions = ["online", "offline", "busy"] as const;

export type BarWithUser = Bar & {
  user: {
    id: string;
    username: string;
    avatarUrl: string | null;
    membershipTier: string;
    isOwner?: boolean;
    level?: number;
  };
  adoptionCount?: number;
  adoptedFromBarId?: string | null;
  parentBar?: {
    id: string;
    content: string;
    user: {
      id: string;
      username: string;
      avatarUrl: string | null;
    };
  } | null;
  replyCount?: number;
};

export const categoryOptions = [
  "Funny",
  "Serious",
  "Wordplay",
  "Storytelling",
  "Battle",
  "Freestyle",
] as const;
export const membershipTiers = ["free", "donor", "donor_plus"] as const;

export const maintenanceStatus = pgTable("maintenance_status", {
  id: varchar("id").primaryKey().default("singleton"),
  isActive: boolean("is_active").notNull().default(false),
  message: text("message"),
  activatedAt: timestamp("activated_at"),
  activatedBy: varchar("activated_by").references(() => users.id),
});

export type MaintenanceStatus = typeof maintenanceStatus.$inferSelect;

// Protected Bars - Owner's secret backlog that prevents others from posting matching content
export const protectedBars = pgTable("protected_bars", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  content: text("content").notNull(), // The protected bar content
  notes: text("notes"), // Owner's private notes about this bar
  similarityThreshold: integer("similarity_threshold").notNull().default(80), // 0-100% similarity required to block
  createdAt: timestamp("created_at").notNull().defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
});

export const protectedBarsRelations = relations(protectedBars, ({ one }) => ({
  creator: one(users, {
    fields: [protectedBars.createdBy],
    references: [users.id],
  }),
}));

export type ProtectedBar = typeof protectedBars.$inferSelect;
export type InsertProtectedBar = typeof protectedBars.$inferInsert;

export const insertProtectedBarSchema = createInsertSchema(protectedBars).omit({
  id: true,
  createdAt: true,
});

// AI Settings - Owner configurable AI abilities
export const aiSettings = pgTable("ai_settings", {
  id: varchar("id")
    .primaryKey()
    .default(sql`'default'`),
  // Feature toggles
  moderationEnabled: boolean("moderation_enabled").notNull().default(true),
  styleAnalysisEnabled: boolean("style_analysis_enabled")
    .notNull()
    .default(true),
  orphieChatEnabled: boolean("orphie_chat_enabled").notNull().default(true),
  barExplanationsEnabled: boolean("bar_explanations_enabled")
    .notNull()
    .default(true),
  rhymeSuggestionsEnabled: boolean("rhyme_suggestions_enabled")
    .notNull()
    .default(true),
  // Moderation settings
  moderationStrictness: text("moderation_strictness")
    .notNull()
    .default("balanced"), // "lenient", "balanced", "strict"
  autoApproveEnabled: boolean("auto_approve_enabled").notNull().default(true),
  // Orphie customization
  orphiePersonality: text("orphie_personality"), // Custom personality for text mode
  voiceModePersonality: text("voice_mode_personality"), // Custom personality for voice mode
  orphieGreeting: text("orphie_greeting"), // Custom greeting message
  // Rate limits (per user per hour)
  chatRateLimit: integer("chat_rate_limit").notNull().default(50),
  explanationRateLimit: integer("explanation_rate_limit").notNull().default(30),
  suggestionRateLimit: integer("suggestion_rate_limit").notNull().default(20),
  // Timestamps
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  updatedBy: varchar("updated_by").references(() => users.id),
});

export type AISettings = typeof aiSettings.$inferSelect;
export type InsertAISettings = typeof aiSettings.$inferInsert;

// Notebooks - User writing documents
export const notebooks = pgTable("notebooks", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull().default("Untitled"),
  content: text("content").notNull().default(""),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const notebooksRelations = relations(notebooks, ({ one }) => ({
  user: one(users, { fields: [notebooks.userId], references: [users.id] }),
}));

export type Notebook = typeof notebooks.$inferSelect;
export type InsertNotebook = z.infer<typeof insertNotebookSchema>;

export const insertNotebookSchema = createInsertSchema(notebooks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Message of the Day table
export const messageOfTheDay = pgTable("message_of_the_day", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  message: text("message").notNull(),
  isActive: boolean("is_active").notNull().default(true),
  createdBy: varchar("created_by").notNull().references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const messageOfTheDayRelations = relations(messageOfTheDay, ({ one }) => ({
  creator: one(users, {
    fields: [messageOfTheDay.createdBy],
    references: [users.id],
  }),
}));

export const insertMessageOfTheDaySchema = createInsertSchema(messageOfTheDay).pick({
  message: true,
  isActive: true,
});

// Custom backgrounds table
export const customBackgrounds = pgTable("custom_backgrounds", {
  sid: varchar("sid")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  imageUrl: text("image_url").notNull(),
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  createdBy: varchar("created_by").notNull().references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const customBackgroundsRelations = relations(customBackgrounds, ({ one }) => ({
  creator: one(users, {
    fields: [customBackgrounds.createdBy],
    references: [users.id],
  }),
}));

export const insertCustomBackgroundSchema = createInsertSchema(customBackgrounds).pick({
  name: true,
  imageUrl: true,
  isActive: true,
  sortOrder: true,
});

// Site-wide settings table (key-value store for owner-controlled settings)
export const siteSettings = pgTable("site_settings", {
  key: varchar("key", { length: 255 }).primaryKey(),
  value: text("value").notNull(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// ==========================================
// BEATS, VAULT, SONGS — Phase 1 Redesign
// ==========================================

// Beats — uploaded audio tracks from producers
export const beats = pgTable("beats", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  producerId: varchar("producer_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  audioUrl: text("audio_url").notNull(),
  waveformData: jsonb("waveform_data"), // Array of ~200 normalized peak values (0-1)
  coverArtUrl: text("cover_art_url"),
  bpm: integer("bpm"),
  key: text("key"), // e.g. "C minor", "F# major"
  genre: text("genre"), // e.g. "trap", "boom_bap", "drill"
  tags: text("tags").array(),
  duration: integer("duration"), // seconds
  description: text("description"),
  credits: text("credits"),
  licenseType: text("license_type").notNull().default("preview_only"),
  status: text("status").notNull().default("approved"),
  plays: integer("plays").notNull().default(0),
  favoritesCount: integer("favorites_count").notNull().default(0),
  isPublic: boolean("is_public").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const beatsRelations = relations(beats, ({ one, many }) => ({
  producer: one(users, {
    fields: [beats.producerId],
    references: [users.id],
  }),
  favorites: many(beatFavorites),
}));

export type Beat = typeof beats.$inferSelect;
export type InsertBeat = typeof beats.$inferInsert;

export const insertBeatSchema = createInsertSchema(beats).omit({
  id: true,
  plays: true,
  favoritesCount: true,
  createdAt: true,
  updatedAt: true,
});

// Beat favorites — users can favorite beats for quick access
export const beatFavorites = pgTable(
  "beat_favorites",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    beatId: varchar("beat_id")
      .notNull()
      .references(() => beats.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [unique("beat_favorites_unique").on(table.userId, table.beatId)],
);

export const beatFavoritesRelations = relations(beatFavorites, ({ one }) => ({
  user: one(users, { fields: [beatFavorites.userId], references: [users.id] }),
  beat: one(beats, { fields: [beatFavorites.beatId], references: [beats.id] }),
}));

// Folders — organize bars in the Vault
export const folders = pgTable("folders", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  description: text("description"),
  position: integer("position").notNull().default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const foldersRelations = relations(folders, ({ one, many }) => ({
  user: one(users, { fields: [folders.userId], references: [users.id] }),
  folderBars: many(folderBars),
}));

export type Folder = typeof folders.$inferSelect;
export type InsertFolder = typeof folders.$inferInsert;

export const insertFolderSchema = createInsertSchema(folders).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Folder bars — junction table linking bars to folders
export const folderBars = pgTable(
  "folder_bars",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    folderId: varchar("folder_id")
      .notNull()
      .references(() => folders.id, { onDelete: "cascade" }),
    barId: varchar("bar_id")
      .notNull()
      .references(() => bars.id, { onDelete: "cascade" }),
    position: integer("position").notNull().default(0),
    addedAt: timestamp("added_at").notNull().defaultNow(),
  },
  (table) => [unique("folder_bars_unique").on(table.folderId, table.barId)],
);

export const folderBarsRelations = relations(folderBars, ({ one }) => ({
  folder: one(folders, { fields: [folderBars.folderId], references: [folders.id] }),
  bar: one(bars, { fields: [folderBars.barId], references: [bars.id] }),
}));

// Bar versions — auto-saved edit history
export const barVersions = pgTable("bar_versions", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  barId: varchar("bar_id")
    .notNull()
    .references(() => bars.id, { onDelete: "cascade" }),
  content: text("content").notNull(),
  versionNumber: integer("version_number").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const barVersionsRelations = relations(barVersions, ({ one }) => ({
  bar: one(bars, { fields: [barVersions.barId], references: [bars.id] }),
}));

export type BarVersion = typeof barVersions.$inferSelect;

// Songs — multi-section compositions built in Song Builder
export const songs = pgTable("songs", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull().default("Untitled"),
  beatId: varchar("beat_id").references(() => beats.id, { onDelete: "set null" }),
  status: text("status").notNull().default("draft"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const songsRelations = relations(songs, ({ one, many }) => ({
  user: one(users, { fields: [songs.userId], references: [users.id] }),
  beat: one(beats, { fields: [songs.beatId], references: [beats.id] }),
  sections: many(songSections),
}));

export type Song = typeof songs.$inferSelect;
export type InsertSong = typeof songs.$inferInsert;

export const insertSongSchema = createInsertSchema(songs).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Song sections — individual blocks within a song (verse, hook, etc.)
export const songSections = pgTable("song_sections", {
  id: varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`),
  songId: varchar("song_id")
    .notNull()
    .references(() => songs.id, { onDelete: "cascade" }),
  type: text("type").notNull(), // "intro" | "verse" | "hook" | "bridge" | "outro" | "ad_lib"
  label: text("label"), // Custom label, e.g., "Verse 1"
  content: text("content").notNull().default(""),
  position: integer("position").notNull().default(0),
  sourceBarIds: text("source_bar_ids").array(), // Bar IDs pulled from vault
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const songSectionsRelations = relations(songSections, ({ one }) => ({
  song: one(songs, { fields: [songSections.songId], references: [songs.id] }),
}));

export type SongSection = typeof songSections.$inferSelect;
export type InsertSongSection = typeof songSections.$inferInsert;

export const insertSongSectionSchema = createInsertSchema(songSections).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Composite types for API responses
export type BeatWithProducer = Beat & {
  producer: {
    id: string;
    username: string;
    avatarUrl: string | null;
    producerVerified: boolean;
  };
  isFavorited?: boolean;
};

export type SongWithSections = Song & {
  sections: SongSection[];
  beat?: BeatWithProducer | null;
};

export type FolderWithCount = Folder & {
  barCount: number;
};
